<template>
  <main
    v-if="offer && product"
    class="product-detail"
    data-page-type="shop"
  >
    <GridContainer
      class="product-detail__header"
      padding
    >
      <div class="product-detail__title-block">
        <CdnImage
          v-if="brand?.logo"
          :class="[
            'product-detail__logo-image',
            'filter-invert ',
            `product-detail__logo-image--${brand.slug}`,
          ]"
          render-without-figure
          :src="brand.logo"
          preload
          :image-style="`aspect-ratio: ${brand.aspectRatio}`"
          :alt="`${brand.name} Logo`"
        />
        <Heading
          align="center"
          size="xl"
          tag="h1"
          class="product-detail__title"
        >
          {{ product.family?.name }}
          <span class="text--center text--default product-detail__variant">
            <FootnoteText :text="standardProduct?.variantName || ''" />
          </span>
        </Heading>
      </div>
      <SquareBadgeContainer
        :offer="offer"
        class="product-detail__badge"
      />
      <ImageGalleryContainer
        v-if="hasRelevantImages"
        :media-data="relevantImages"
        preload
        class="product-detail__image-gallery"
      />
      <div class="product-detail__offer">
        <TreueshopIcon
          v-if="hasTreueshopIcon"
          class="product-detail__treueshop-icon"
        />
        <RebateHighlight :article-number="product.articleNumber ?? undefined" />

        <OfferConfigurator
          :bundle-items="transformedAlternatives"
          :focused-item-index="initialFocusesItemIndex"
          class="product-detail__variants"
          @open-bundle-item-config="openBundleItemConfig"
          @select-variant="handleVariantChange"
        />

        <div class="product-detail__offer-details">
          <EnergyLabel
            :product="product"
            class="product-detail__energy-label"
            datasheet-alignment="left"
          />
          <KameraLink
            v-if="offer"
            :offer="offer"
          />
          <PricesContainer
            v-if="offerPrices"
            :legal-texts="product.legalTexts"
            show-highlight
            :prices-data="offerPrices"
            class="product-detail__prices"
          />
          <FinancingCalculator
            v-if="hasZeroPercentFinancing"
            :price="defaultPrice * 100"
            visual="product-page"
            is-collapsable
          />
          <div
            v-if="telekomContractLink && !hideContractLink"
            class="product-detail__buy-with-contract"
          >
            <LinkButton
              tag="a"
              is-external
              text
              :href="telekomContractLink"
              tealium-name="zu-den-details"
              :tealium-suffix="product.articleNumber || undefined"
            >
              {{ contractLinkText }}
            </LinkButton>
          </div>
          <AddToCartPromptContainer
            :offer="offer"
            class="product-detail__add-to-cart-prompt"
          />
        </div>

        <div class="product-detail__availability">
          <AvailabilityInformationContainer
            v-if="product.articleNumber && product.availability"
            :article-number="product.articleNumber"
            :availability="product.availability"
          />
          <FulfillerInfo
            v-if="provider"
            :provider="provider"
            :is-freight-forwarding-service="isFreightForwardingService"
          />
        </div>
      </div>

      <CompatibilityQuickView
        class="product-detail__compatibility"
        :offer="offer"
      />
    </GridContainer>

    <GridContainer padding>
      <Heading
        align="center"
        size="lg"
        tag="h2"
        spaces-bottom
      >
        {{ product.family?.name }} auf einen Blick
      </Heading>

      <ProductTabs :offer="offer" />
      <!-- Product is bundle, the Bundle may have a description -->
      <ContentToggle v-if="product.description && product.bundle">
        <div
          class="product-detail__description"
          v-html="replaceFootnotes(product.description)"
        ></div>
      </ContentToggle>
    </GridContainer>

    <section
      v-if="accessoryOffers.length || recommendedOffers.length"
      class="product-detail__recommendations"
    >
      <GridContainer padding>
        <AccordionItem
          v-if="accessoryOffers.length"
          is-open-on-desktop
          is-small
        >
          <template #title>
            <Heading
              align="center"
              class="accordion-legacy__headline"
              size="lg"
              spaces-bottom
              tag="h2"
            >
              Passendes Zubehör
            </Heading>
          </template>
          <template #content>
            <OfferTileContainer
              data-test-id="accessory"
              :offers="accessoryOffers"
            />
          </template>
        </AccordionItem>
        <AccordionItem
          v-if="recommendedOffers.length"
          is-open-on-desktop
          is-small
        >
          <template #title>
            <Heading
              align="center"
              class="accordion-legacy__headline"
              size="lg"
              tag="h2"
              spaces-bottom
            >
              Das könnte Dich auch interessieren
            </Heading>
          </template>
          <template #content>
            <OfferTileContainer
              data-test-id="recommendation"
              :offers="recommendedOffers"
            />
          </template>
        </AccordionItem>
      </GridContainer>
    </section>

    <VisitedOfferTileContainer
      v-if="lastSeenOffersLength > 1"
      :max-size="4"
      :viewed-article-number="product.articleNumber"
      has-grid-padding
    />
    <CartSticky
      v-if="offerPrices"
      :prices="offerPrices"
      class="product-detail__cart-sticky"
    >
      <template #addToCart>
        <AddToCartButtonContainer
          :display-cart-quantity="true"
          :display-text="false"
          :offer="offer"
          variant="outline-primary"
        />
      </template>
    </CartSticky>
  </main>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from '#imports';
import { storeToRefs } from 'pinia';
import {
  replaceFootnotes,
  getPrice,
  type LocalImage,
  type ScopedOfferAlternative,
} from '@i22team/rocket';
import { getPricesFromOfferWithRebate } from '~/lib/price-from-offer';
import { getTelekomContractLink, getTelekomContractLinkText } from '~/lib/telekom-contract-link';
import { getBrand } from '~/lib/brand';
import CdnImage from '~/components/cdn-image.vue';

import {
  Heading,
  GridContainer,
  OfferConfigurator,
  CartSticky,
  AccordionItem,
  EnergyLabel,
  LinkButton,
  FootnoteText,
} from '@i22team/td-longtail-vue-components';
import type { VariantSelect } from '@i22team/td-longtail-vue-components/dist/src/components/offer-configurator/select.type';

// container buis logic: component library implemented
import PricesContainer from '~/components/product-detail/prices-container.vue';
import AvailabilityInformationContainer from '~/components/product-detail/availability-information-container.vue';
import ImageGalleryContainer from '~/components/product-detail/image-gallery-container.vue';
import AddToCartPromptContainer from '~/components/product-detail/add-to-cart-prompt-container.vue';
// internal components
import FulfillerInfo from '~/components/product-detail/fulfiller-info.vue';
import OfferTileContainer from '~/components/offer-tiles-container.vue';
import ProductTabs from '~/components/product-detail/tabs/product-tabs.vue';
import ContentToggle from '~/components/content-toggle.vue';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';
import FinancingCalculator from '~/components/financing-calculator.vue';
import CompatibilityQuickView from '~/components/compatibility-quick-view.vue';
import { getImagesFromOffer } from '~/lib/goliath-media';
import TreueshopIcon from '~/components/treueshop-icon.vue';
import SquareBadgeContainer from '~/components/product-detail/square-badge-container.vue';
import KameraLink from '~/components/kamera-link.vue';
import { trackFBEvent } from '~/lib/tracking-fb';
import type { TransformedAlternative } from '@i22team/rocket/dist/transform-offer-alternatives';
import { useLastSeenOffersStore } from '~/stores/last-seen-offers';
import { useOfferStore } from '~/stores/offers';
import { useCouponStore } from '~/stores/coupon';
import RebateHighlight from '~/components/product-detail/rebate-highlight.vue';

export type ProductDetailProps = {
  offer?: OfferWithProduct;
  transformedAlternatives?: TransformedAlternative<ScopedOfferAlternative>[];
  additionalImages?: LocalImage[];
  hideContractLink?: boolean;
};

const props = withDefaults(defineProps<ProductDetailProps>(), {
  offer: undefined,
  transformedAlternatives: undefined,
  additionalImages: undefined,
});

const lastSeenOfferStore = useLastSeenOffersStore();

const { lastSeenOffersArticleNumbers } = storeToRefs(lastSeenOfferStore);

const { rebates, zeroPercentFinancing } = storeToRefs(useOfferStore());
const {
  getCoupon,
  isValidCoupon,
  incompatibleArticleNumbers,
  compatibleArticleNumbers,
  compatibleProductProviders,
} = storeToRefs(useCouponStore());

const $route = useRoute();
const $router = useRouter();

const initialFocusesItemIndex = ref(parseInt($route.hash.replace(/#/, ''), 10) || undefined);
const focusedItemIndex = ref<number | undefined>(undefined);

const VisitedOfferTileContainer = defineAsyncComponent(
  () => import('~/components/visited-offer-tiles.vue')
);

const product = computed(() => props.offer?.product || undefined);
const bundleProduct = computed(() => getBundleProduct(product.value));
const standardProduct = computed(() => getStandardProduct(product.value));

const hasZeroPercentFinancing = computed(() =>
  zeroPercentFinancing.value.includes(product.value?.articleNumber || '')
);

const offerPrices = computed(() =>
  props.offer
    ? getPricesFromOfferWithRebate(props.offer, rebates.value[product.value?.articleNumber || ''])
    : undefined
);

const defaultPrice = computed(() => getPrice(offerPrices.value, 'DEFAULT', 'ONETIME')?.value);
const relevantImages = computed(() => [
  ...(props.additionalImages || []),
  ...getImagesFromOffer(props.offer),
]);
const hasRelevantImages = computed(() => relevantImages.value.length > 0);

const accessoryOffers = computed(() => {
  const accessories = props.offer?.accessories;

  if (!accessories) {
    return [];
  }

  return accessories.slice(0, 4);
});
const recommendedOffers = computed(() => {
  const recommendations = props.offer?.recommendations;

  if (!recommendations) {
    return [];
  }

  return recommendations.slice(0, 4);
});
const lastSeenOffersLength = computed(() => lastSeenOffersArticleNumbers.value.length);

const telekomContractLink = computed(() =>
  props.offer ? getTelekomContractLink(props.offer) : undefined
);
const hasSmartphoneCategory = computed(() => {
  const productCategories = product.value?.family?.categories;
  if (!productCategories) return false;
  return productCategories.some((category) => category?.categoryNumber === 'T_EGHANDYS');
});
const contractLinkText = computed(() =>
  getTelekomContractLinkText(null, hasSmartphoneCategory.value)
);
const isCouponCompatible = computed(() => {
  if (compatibleArticleNumbers.value?.length > 0) {
    return compatibleArticleNumbers.value.includes(product.value?.articleNumber || '');
  }
  if (compatibleProductProviders.value?.length > 0) {
    if (!product.value?.availability?.provider) return false;
    return compatibleProductProviders.value.includes(product.value.availability.provider);
  }
  if (incompatibleArticleNumbers.value?.length > 0) {
    return !incompatibleArticleNumbers.value.includes(product.value?.articleNumber || '');
  }
  return true;
});
const allowDiscountedProduct = computed(() => {
  if (getCoupon.value?.type === 'coupon') {
    return !props.offer?.prices?.some?.((price) => price?.type?.toLowerCase() === 'was');
  }
  return true;
});
const hasTreueshopIcon = computed(
  () => isCouponCompatible.value && isValidCoupon.value && allowDiscountedProduct.value
);
const provider = computed(() => product.value?.availability?.provider || undefined);
// Samsung TV will be shipped by Freight Forwarding
const isFreightForwardingService = computed(() =>
  ['8806095474939', '8806095593715', '8806095593692'].includes(product.value?.articleNumber || '')
);
const brand = computed(() => getBrand(props.offer));

const addOfferToLastSeen = () => {
  if (product.value?.articleNumber) {
    lastSeenOfferStore.initLastSeenOffers(product.value.articleNumber);
  }
};

onMounted(() => {
  addOfferToLastSeen();
  trackFBEvent('ViewContent', {
    content_ids: [product.value?.articleNumber],
    content_name: product.value?.name,
    content_type: 'product',
    currency: 'EUR',
  });
});
onBeforeMount(() => {
  document.body.classList.add('sticky-cart');
});
onBeforeUnmount(() => {
  document.body.classList.remove('sticky-cart');
});

const handleVariantChange = ({ variant }: VariantSelect) => {
  const index = focusedItemIndex.value || initialFocusesItemIndex.value;
  // Catch exception to avoid NavigationDuplicated issue
  $router.replace(`${variant.path}${index ? `#${index}` : ''}`).catch(() => {});
};

const openBundleItemConfig = ({ position }: { position: number }) => {
  focusedItemIndex.value = position;
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
@use 'assets/selectors/pattern/lists';

.product-detail {
  padding-top: 1.5 * $grid-base;

  &__header {
    margin-bottom: $grid-base * 4;

    @include mq($mq-md) {
      display: grid;
      grid-gap: 0 $grid-base * 2;
      grid-template-columns: repeat(2, calc(50% - $grid-base));
      grid-template-areas:
        'header header'
        'badge badge'
        'image offer'
        'compatibility offer';
      margin-bottom: $grid-base * 10;
    }
  }

  &__title-block {
    margin-bottom: 1rem;
    grid-area: header;
  }

  &__badge {
    margin-bottom: 1.5rem;
    grid-area: badge;
  }

  &__compatibility {
    padding-top: $grid-base * 2;
    grid-area: compatibility;
  }

  &__offer {
    grid-area: offer;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__variants {
    :deep(.single-item-configurator__variant-group),
    :deep(.bundle-configurator) {
      padding-bottom: 0;
      margin-bottom: $grid-base * 2;
    }
  }

  &__image-gallery {
    grid-area: image;
    @include mq(false, $mq-md - 1) {
      margin-bottom: 2 * $grid-base;
    }
  }

  &__logo-image {
    height: 20px;
    display: block;
    margin: 0 auto $grid-base;
  }

  &__buy-with-contract {
    margin-bottom: $grid-base;
    @include mq($mq-sm) {
      margin-bottom: $grid-base * 2;
    }
  }

  &__energy-label {
    --max-list-width: 220px;
    margin-bottom: $grid-base;
    @include font-size(36px, 36px);
    @include mq($mq-sm) {
      --max-list-width: none;
      margin-bottom: 2 * $grid-base;
      @include font-size(48px, 48px);
    }
  }

  :deep(.youtube-video) {
    margin-top: 1.5 * $grid-base;
    margin-bottom: 0;

    @include mq($mq-md) {
      margin-top: 0;
    }
  }

  &__description {
    columns: 456px 3;
    column-gap: 24px;

    @include mq($mq-sm) {
      margin-bottom: 4 * $grid-base;
    }

    :deep(ul) {
      margin-bottom: 1.5 * $grid-base;
      @extend .list;

      li {
        @extend .list__element;
        @extend .list__element--dashed;
      }
    }
  }

  &__variant {
    display: block;
    margin-bottom: 0;
    @include mq(false, $mq-md - 1) {
      display: none;
    }
  }

  &__cart-sticky {
    z-index: $z-index-cart-sticky;
    @include mq($mq-md) {
      visibility: hidden;
    }
  }

  &__availability {
    margin-bottom: $grid-base;
  }

  &__treueshop-icon {
    display: block;
    width: 50%;
    align-self: flex-end;
    margin-right: $grid-base;
    max-width: 250px;

    @include mq($mq-sm) {
      align-self: flex-start;
    }
  }

  &__prices {
    margin-bottom: $grid-base;

    @include mq($mq-sm) {
      margin-bottom: $grid-base * 2;
    }
  }

  &__recommendations {
    margin-bottom: 3rem;
  }
}
</style>

<style>
.sticky-cart {
  padding-bottom: 61px;
}

@media (min-width: 1024px) {
  .sticky-cart {
    padding-bottom: 0;
  }
}
</style>
